// Welke velden van een registratieformulier krijgt de invuller te zien?
//
// Twee regels, voor het openbare formulier (`EventOpen`) en het formulier in de
// app (`AddEvent`) samen:
//
// 1. Alleen velden met een type waar een invulveld voor is
//    (`isRenderableEventField`).
// 2. Het losse deelnemersveld (`Type: 'participants'`) is een overblijfsel: de
//    bouwer biedt het niet meer aan, en aanwezigheid wordt nu per output
//    gevraagd ('Wie waren aanwezig?'). Vraagt het outputveld van dit formulier
//    daar al naar, dan verbergen we het oude veld — anders zag de invuller twee
//    keer dezelfde namenlijst (testronde 2026-10-28, Fatima B3). Vraagt de
//    output het niet (deelnemersmodule uit, koppelen uitgezet, of de output
//    wordt elders gemeten), dan blijft het oude veld staan: het is dan de enige
//    plek waar aanwezigheid kan worden ingevuld.
//
// De voorwaarde voor 'de output vraagt het' is dezelfde als in beide formulieren:
// `personaOn === 'flex'`, de output wordt op het formulier gemeten, en
// `LinkParticipantsToOutputs` staat niet uit.
// AI-CONTEXT: See docs/test-runs/2026-10-28-formulier-openen-en-qr-link.md

import isRenderableEventField from './isRenderableEventField'
import { isFlagOff } from '../isFlagOn'

/**
 * @typedef {object} OutputDoc
 * @property {string} ID
 * @property {string} [MeasurementMethod] - 'event_form' (standaard) of elders gemeten
 * @property {*} [LinkParticipantsToOutputs] - Aan/uit-vlag in elke opslagvorm
 */

/**
 * @param {Array<OutputDoc>} outputs
 * @param {string} personaOn - `useSettings().personasOn`
 * @returns {boolean} True als minstens één output op dit formulier naar aanwezigen vraagt.
 */
const outputsAskAttendance = (outputs, personaOn) => (
    personaOn === 'flex'
    && outputs.some((output) => (
        (output.MeasurementMethod || 'event_form') === 'event_form'
        && !isFlagOff(output.LinkParticipantsToOutputs)
    ))
)

/**
 * @param {Array<object> | string | undefined} fields - EventFields van het formulier ('' = laden)
 * @param {object} context
 * @param {Array<OutputDoc> | string | undefined} context.outputs - Outputs van de activiteit
 * @param {string} context.personaOn - `useSettings().personasOn`
 * @returns {Array<object>} De velden die de invuller ziet, in dezelfde volgorde.
 */
const visibleEventFields = (fields, { outputs, personaOn }) => {
    const renderable = (Array.isArray(fields) ? fields : []).filter(isRenderableEventField)
    const hasOutputsField = renderable.some((field) => field.Type === 'outputs')
    const outputList = Array.isArray(outputs) ? outputs : []

    if (!hasOutputsField || !outputsAskAttendance(outputList, personaOn)) return renderable
    return renderable.filter((field) => field.Type !== 'participants')
}

export default visibleEventFields
